// ============================================================================
// MediaPlayer — ЕДИНЫЙ SSoT проигрыватель платформы (аудио/видео).
// P0 (по ревизии Луны): streaming-first — MP4/WebM играются прямо с
// /files/{id} (Range, http.ServeContent) с токеном в query; офлайн — blob из
// кэша (ensureFileBlob). Аудио: серверный waveform (клик = seek), скорость,
// mute, скачать. MediaSession — локскрин/наушники. «Играет только один» —
// lib/mediaPlayback. footer — расширения (распознанный текст).
// P1 (медиа-хвосты): скорость ПОМНИТСЯ (user_settings media.speed) и
// применяется ко всем новым воспроизведениям; видео — «картинка в картинке»
// (поддержка браузера); очередь «слушать подряд» — родитель просит автозапуск
// (autoPlay/onEnded), пауза/перемотка пользователя отменяет её (onInterrupt).
// ВИДЕО-КОДЕК: если браузер не умеет контейнер/кодек, плеер ОДИН раз
// переключается на серверное перекодирование (`?transcode=mp4`) и показывает
// «Готовим видео…»; окончательная ошибка — с кнопкой скачивания оригинала.
// ============================================================================

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Download, Gauge, Pause, Play, Volume2, VolumeX } from 'lucide-react';
import { claimPlayback, mediaPlaybackId, releasePlayback } from '@/lib/mediaPlayback';
import { ensureFileBlob } from '@/lib/fileSync';
import { fileBase64 } from '@/lib/fileBytes';
import { downloadBytes } from '@/lib/printForm';
import { logErrorToJournal, friendlyError } from '@/lib/errors';
import { getSDK } from '@/store/sdkStore';
import { useAuthStore } from '@/store/authStore';
import { useCenterStore } from '@/store/centerStore';
import { createLogger } from '@/lib/logger';
import {
  clampMediaSpeed, getMediaSpeed, MEDIA_RATES, setMediaSpeed, subscribeMediaSettings,
} from '@/lib/mediaSettings';
import { fmt, Waveform, type MediaElementHandlers } from '@/components/media/mediaPlayerParts';
import { MediaPlayerApiContext } from '@/components/media/mediaPlayerContext';
import { MediaVideoSurface } from '@/components/media/MediaPlayerVideo';
import {
  canPlayVideoContainer, hasTranscodeParam, isFileStreamUrl, transcodeUrl,
} from '@/components/media/videoSource';

const log = createLogger('media');

interface Props {
  /** Готовый URL (blob:/data:) — если известен. */
  src?: string;
  /** Либо fileId — URL резолвится: онлайн — стриминг /files/{id} (Range),
   *  офлайн — blob из кэша (ensureFileBlob). */
  fileId?: string;
  kind: 'audio' | 'video';
  /** Вид аудио: 'voice' — компактное голосовое сообщение (без имени/скорости),
   *  'audio' — обычный аудиофайл (mp3/m4a/ogg…) с waveform и именем. */
  variant?: 'audio' | 'voice';
  name?: string;
  /** Стиль «моё» (в чате) — рамка primary. */
  mine?: boolean;
  compact?: boolean;
  onDownload?: () => void;
  /** Расширение снизу (распознанный текст); API плеера — в MediaPlayerApiContext. */
  footer?: ReactNode;
  /** Очередь «слушать подряд»: автозапуск этого медиа. */
  autoPlay?: boolean;
  /** Конец воспроизведения (очередь запросит следующее аудио ленты). */
  onEnded?: () => void;
  /** Пауза/перемотка/остановка (в т.ч. запуск другого медиа) — отмена автозапуска. */
  onInterrupt?: () => void;
  /** Автозапуск состоялся — очередь сбрасывает цель (повторный монтаж не играет). */
  onAutoplayConsumed?: () => void;
}

/** Стриминг-URL файла: токен в query (медиа-элементы не шлют Authorization).
 *  base добавляется явно — медиа-элемент не может передать X-Hydra-Base, а
 *  сервер резолвит базу для /files/ по ?base= (иначе «file not found»). */
export function fileStreamUrl(fileId: string, token: string, baseSlug?: string | null): string {
  const base = baseSlug ? `&base=${encodeURIComponent(baseSlug)}` : '';
  return `/files/${encodeURIComponent(fileId)}?token=${encodeURIComponent(token)}${base}`;
}

export function MediaPlayer({ src, fileId, kind, variant = 'audio', name, mine, compact, onDownload, footer, autoPlay, onEnded, onInterrupt, onAutoplayConsumed }: Props) {
  const token = useAuthStore((s) => s.token) || '';
  const [url, setUrl] = useState(src || '');
  const [peaks, setPeaks] = useState<number[]>([]);
  const [error, setError] = useState('');
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  // Скорость: старт — из запомненной (media.speed), диапазон 0.5–3×.
  const [rate, setRate] = useState(() => getMediaSpeed());
  const [muted, setMuted] = useState(false);
  const mediaRef = useRef<HTMLMediaElement>(null);
  const idRef = useRef(mediaPlaybackId());
  const objectUrlRef = useRef('');
  const autoPlayedRef = useRef(false);
  // Колбэки очереди — через ref, чтобы события (пауза/стоп чужого медиа)
  // не зависели от момента последнего рендера.
  const interruptRef = useRef(onInterrupt);
  interruptRef.current = onInterrupt;
  const consumedRef = useRef(onAutoplayConsumed);
  consumedRef.current = onAutoplayConsumed;
  /** Один раз на источник: blob из кэша чтения/SDK (офлайн или стриминг не отдался). */
  const triedCacheRef = useRef(false);
  /** Перекодирование запрошено — второго раза нет (без бесконечных повторов). */
  const transcodeTriedRef = useRef(false);
  /** После переключения источника продолжить воспроизведение (если играло/просили). */
  const resumeAfterFallbackRef = useRef(false);
  /** Пользователь/очередь просили воспроизведение — продолжить и после смены источника. */
  const playIntentRef = useRef(false);
  /** Идёт подготовка перекодированного видео — «Готовим видео…». */
  const [preparing, setPreparing] = useState(false);
  const [savingOriginal, setSavingOriginal] = useState(false);

  const loadCachedSource = useCallback(async (): Promise<boolean> => {
    if (!fileId || triedCacheRef.current) return false;
    triedCacheRef.current = true;
    try {
      const { blob } = await ensureFileBlob(fileId);
      const u = URL.createObjectURL(blob);
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = u;
      setUrl(u);
      setError('');
      return true;
    } catch (e: unknown) {
      setError(String((e as Error)?.message || e) || 'файл недоступен');
      return false;
    }
  }, [fileId]);

  /** Один раз на источник: переключение на серверное перекодирование.
   *  Возвращает true, если источник уже заменён (повторно не пробуем). */
  const fallbackToTranscode = useCallback((): boolean => {
    if (kind !== 'video' || transcodeTriedRef.current) return false;
    if (!fileId || !isFileStreamUrl(url) || hasTranscodeParam(url)) return false;
    const next = transcodeUrl(url);
    if (!next) return false;
    transcodeTriedRef.current = true;
    setError('');
    setPreparing(true);
    setUrl(next);
    return true;
  }, [kind, fileId, url]);

  /** Скачивание ОРИГИНАЛА: кнопка ошибки должна работать и без onDownload
   *  (в чате вложение не передаёт обработчик). */
  const saveOriginal = useCallback(async () => {
    if (onDownload) {
      onDownload();
      return;
    }
    if (!fileId || savingOriginal) return;
    setSavingOriginal(true);
    try {
      const r = await getSDK().files.downloadFile({ id: fileId });
      const b64 = fileBase64(r?.data);
      if (!b64) throw new Error('файл пуст');
      downloadBytes(name || (kind === 'video' ? 'video' : 'audio'), 'application/octet-stream', b64);
    } catch (e: unknown) {
      logErrorToJournal(e, 'Проигрыватель: скачивание оригинала');
      setError(friendlyError(e));
    } finally {
      setSavingOriginal(false);
    }
  }, [onDownload, fileId, name, savingOriginal, kind]);

  // Резолв URL: онлайн — СТРИМИНГ с /files/{id} (Range, без blob-памяти);
  // офлайн — blob из кэша чтения (SSoT ensureFileBlob).
  useEffect(() => {
    // Новый источник — сбрасываем однократные попытки (кэш/перекодирование).
    triedCacheRef.current = false;
    transcodeTriedRef.current = false;
    resumeAfterFallbackRef.current = false;
    setPreparing(false);
    if (src) {
      setUrl(src);
      return;
    }
    if (!fileId) return;
    const online = typeof navigator === 'undefined' || navigator.onLine !== false;
    if (online && token) {
      // Оригинал первым: если браузер умеет формат — ничего не перекодируем
      // (переключение только по РЕАЛЬНОЙ ошибке воспроизведения).
      setUrl(fileStreamUrl(fileId, token, useCenterStore.getState().baseSlug));
      return () => {
        if (objectUrlRef.current) {
          URL.revokeObjectURL(objectUrlRef.current);
          objectUrlRef.current = '';
        }
      };
    }
    void loadCachedSource();
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = '';
      }
    };
  }, [src, fileId, token, loadCachedSource]);

  // Смена источника (повторное открытие/офлайн→онлайн) — сбрасываем старую
  // ошибку: иначе плеер навсегда показывает «не удалось воспроизвести».
  useEffect(() => {
    setError('');
  }, [url]);

  // «Готовим видео…» не бесконечное: сервер не ответил — честная ошибка с
  // кнопкой скачивания (повторных запросов на перекодирование нет).
  useEffect(() => {
    if (!preparing) return;
    const timer = window.setTimeout(() => {
      const m = mediaRef.current;
      if (m && m.readyState >= 1) return;
      setPreparing(false);
      setError('Не удалось подготовить видео: сервер не ответил. Скачайте файл и откройте в проигрывателе.');
      log.warn('подготовка видео: истекло время ожидания');
    }, 60000);
    return () => window.clearTimeout(timer);
  }, [preparing, url]);

  // Серверный waveform (только аудио, онлайн): «лучше чем у других» —
  // пики видно ДО прослушивания, клик по волне = переход.
  useEffect(() => {
    if (kind !== 'audio' || !fileId || !token) return;
    const online = typeof navigator === 'undefined' || navigator.onLine !== false;
    if (!online) return;
    const baseSlug = useCenterStore.getState().baseSlug;
    const baseParam = baseSlug ? `&base=${encodeURIComponent(baseSlug)}` : '';
    let alive = true;
    fetch(`/files/${encodeURIComponent(fileId)}/waveform?bins=200&token=${encodeURIComponent(token)}${baseParam}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { bins?: number[] } | null) => {
        if (alive && d && Array.isArray(d.bins)) setPeaks(d.bins);
      })
      .catch((e: unknown) => log.warn('waveform недоступен: ' + String(e)));
    return () => {
      alive = false;
    };
  }, [kind, fileId, token]);

  useEffect(() => {
    const id = idRef.current;
    return () => releasePlayback(id);
  }, []);

  // Память скорости (SSoT — user_settings): изменения из любого места
  // (другой плеер, гидрация настроек) применяются и здесь.
  useEffect(() => subscribeMediaSettings(() => {
    const s = getMediaSpeed();
    setRate((prev) => (prev === s ? prev : s));
  }), []);

  useEffect(() => {
    const m = mediaRef.current;
    if (m) m.playbackRate = rate;
  }, [rate, url]);

  const rememberRate = useCallback((v: number) => {
    const clamped = clampMediaSpeed(v);
    if (clamped !== getMediaSpeed()) setMediaSpeed(clamped);
  }, []);

  // Остановка ЭТОГО проигрывателя (пауза) — общий путь для кнопки и для
  // «играет только один»: пауза по чужому запуску = действие пользователя → отмена автозапуска.
  const stopSelf = useCallback(() => {
    try {
      mediaRef.current?.pause();
    } catch {
      /* элемент удалён */
    }
  }, []);

  const toggle = useCallback(() => {
    const m = mediaRef.current;
    if (!m) return;
    if (m.paused) {
      playIntentRef.current = true;
      claimPlayback(idRef.current, () => {
        stopSelf();
        interruptRef.current?.();
      });
      // Ручной запуск медиа пользователем — запланированный автозапуск
      // следующего аудио снимается (очередь продолжается только сама собой).
      interruptRef.current?.();
      m.play().catch((e: unknown) => {
        log.warn('воспроизведение отклонено: ' + String(e));
        setPlaying(false);
      });
    } else {
      playIntentRef.current = false;
      m.pause();
      releasePlayback(idRef.current);
      interruptRef.current?.();
    }
  }, [stopSelf]);

  // Очередь «слушать подряд»: автозапуск по запросу родителя (одна попытка
  // на активацию; SSoT «играет только один» — через claimPlayback).
  useEffect(() => {
    if (!autoPlay) {
      autoPlayedRef.current = false;
      return;
    }
    if (autoPlayedRef.current) return;
    const m = mediaRef.current;
    if (!m || !url) return;
    autoPlayedRef.current = true;
    playIntentRef.current = true;
    claimPlayback(idRef.current, () => {
      stopSelf();
      interruptRef.current?.();
    });
    try {
      m.playbackRate = rate;
    } catch {
      /* элемент удалён */
    }
    m.play().then(() => {
      consumedRef.current?.();
    }).catch((e: unknown) => {
      log.warn('автозапуск отклонён: ' + String(e));
      setPlaying(false);
      autoPlayedRef.current = false;
    });
  }, [autoPlay, url, rate, stopSelf]);

  const cycleRate = useCallback(() => {
    const m = mediaRef.current;
    const idx = MEDIA_RATES.indexOf(rate);
    const next = MEDIA_RATES[(idx + 1) % MEDIA_RATES.length];
    setRate(next);
    setMediaSpeed(next);
    if (m) m.playbackRate = next;
  }, [rate]);

  const seek = useCallback((value: number) => {
    const m = mediaRef.current;
    if (!m || !Number.isFinite(value)) return;
    m.currentTime = value;
    setCurrent(value);
    // Ручная перемотка — переход пользователя: автозапуск следующего отменяется.
    interruptRef.current?.();
  }, []);

  // MediaSession — локскрин/наушники (P0): метаданные и действия.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;
    const ms = navigator.mediaSession;
    ms.playbackState = playing ? 'playing' : 'paused';
    if (playing) {
      try {
        ms.metadata = new MediaMetadata({
          title: name || (kind === 'video' ? 'Видео' : 'Аудио'),
          artist: 'Hydra',
          artwork: [{ src: '/base-icon', sizes: '512x512', type: 'image/png' }],
        });
      } catch {
        /* MediaMetadata может отсутствовать */
      }
      ms.setActionHandler('play', () => toggle());
      ms.setActionHandler('pause', () => mediaRef.current?.pause());
      ms.setActionHandler('seekbackward', () => {
        const m = mediaRef.current;
        if (m) seek(Math.max(0, m.currentTime - 10));
      });
      ms.setActionHandler('seekforward', () => {
        const m = mediaRef.current;
        if (m) seek(m.currentTime + 10);
      });
      ms.setActionHandler('seekto', (d: MediaSessionActionDetails) => {
        if (d.seekTime != null) seek(d.seekTime);
      });
    }
    return () => {
      try {
        ms.metadata = null;
      } catch {
        /* ok */
      }
    };
  }, [playing, name, kind, toggle, seek]);

  const handlers: MediaElementHandlers = {
    onPlay: () => {
      setPlaying(true);
      setPreparing(false);
    },
    onPause: () => {
      setPlaying(false);
      releasePlayback(idRef.current);
      // Пауза (кнопка, нативные контролы, локскрин) — действие пользователя:
      // очередь «слушать подряд» отменяет запланированный автозапуск.
      interruptRef.current?.();
    },
    onTimeUpdate: () => {
      const m = mediaRef.current;
      setCurrent(m?.currentTime || 0);
      // Обновляем позицию для локскрина (MediaSession)
      if (m && duration > 0 && 'mediaSession' in navigator) {
        try {
          navigator.mediaSession.setPositionState({ duration, position: m.currentTime, playbackRate: m.playbackRate });
        } catch {
          /* некоторые браузеры без setPositionState */
        }
      }
    },
    onLoadedMetadata: () => {
      const m = mediaRef.current;
      setDuration(m?.duration || 0);
      if (m) m.playbackRate = rate;
      setPreparing(false);
      // Перекодированный источник готов — продолжаем, если видео играло
      // (или было запрошено автовоспроизведение) до переключения.
      if (resumeAfterFallbackRef.current && m) {
        resumeAfterFallbackRef.current = false;
        claimPlayback(idRef.current, () => {
          stopSelf();
          interruptRef.current?.();
        });
        m.play().catch((e: unknown) => {
          log.warn('продолжение после перекодирования отклонено: ' + String(e));
          setPlaying(false);
        });
      }
    },
    onEnded: () => {
      playIntentRef.current = false;
      setPlaying(false);
      releasePlayback(idRef.current);
      onEnded?.();
    },
    onError: () => {
      const m = mediaRef.current;
      const code = m?.error?.code ?? 0;
      // 3 — MEDIA_ERR_DECODE, 4 — MEDIA_ERR_SRC_NOT_SUPPORTED,
      // networkState 3 (NETWORK_NO_SOURCE) — источник не найден/битый.
      const unsupported = code === 3 || code === 4 || (code === 0 && m?.networkState === 3);
      // canPlayType: браузер вообще не знает этот контейнер (mov/mkv/avi…).
      const containerUnsupported = kind === 'video' && !canPlayVideoContainer(name);
      if (kind === 'video') {
        // ОДНО переключение на серверное перекодирование (mp4) — только когда
        // проблема в кодеке/контейнере, а не в сети (иначе зря грузим сервер).
        resumeAfterFallbackRef.current = resumeAfterFallbackRef.current
          || playIntentRef.current || playing || !!autoPlay;
        if ((unsupported || containerUnsupported) && fallbackToTranscode()) {
          log.warn(`видео: ошибка ${code}${containerUnsupported ? ', контейнер не поддерживается' : ''} — переключаемся на перекодированный MP4`);
          return;
        }
        // Перекодирование уже пробовали (или оно нечем): качать оригинал
        // целиком в кэш бессмысленно — кодек от этого не появится, а файл
        // может быть очень большим. Честная ошибка с кнопкой скачивания.
        if (unsupported || containerUnsupported || transcodeTriedRef.current) {
          setPreparing(false);
          setError('Видео не воспроизводится: браузер не поддерживает этот формат. Скачайте файл и откройте в проигрывателе.');
          return;
        }
      }
      // Стриминг не отдался (сеть/права/база) — один раз пробуем кэш/SDK.
      if (fileId && isFileStreamUrl(url)) {
        void loadCachedSource();
        return;
      }
      setPreparing(false);
      setError(kind === 'video'
        ? 'Не удалось воспроизвести видео (сеть или файл). Скачайте файл и откройте в проигрывателе.'
        : 'не удалось воспроизвести (формат/файл) — браузер не поддерживает этот кодек');
    },
    // Скорость, изменённая нативными контролами (меню видео) — запоминаем.
    onRateChange: () => {
      const m = mediaRef.current;
      if (m) rememberRate(m.playbackRate);
    },
  };

  if (error) {
    const canDownload = !!onDownload || !!fileId;
    return (
      <div className={`rounded-lg border px-2 py-1.5 text-[11px] text-destructive ${kind === 'video' ? 'bg-muted/30' : ''}`}>
        <div>{error}</div>
        {name && <div className="mt-0.5 truncate text-muted-foreground" title={name}>{name}</div>}
        {canDownload && (
          <button
            type="button"
            className="mt-1 inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] text-foreground hover:bg-muted/50 disabled:opacity-50"
            onClick={() => void saveOriginal()}
            disabled={savingOriginal}
          >
            <Download className="h-3 w-3" /> {savingOriginal ? 'Скачиваем…' : 'Скачать'}
          </button>
        )}
      </div>
    );
  }

  if (kind === 'video') {
    return (
      <div className="w-full overflow-hidden rounded-xl border shadow-sm bg-muted/50">
        <MediaVideoSurface
          url={url}
          name={name}
          mediaRef={mediaRef}
          handlers={handlers}
          preparing={preparing}
          footer={footer}
          onDownload={onDownload || (fileId ? () => void saveOriginal() : undefined)}
        />
      </div>
    );
  }

  const progress = duration > 0 ? current / duration : 0;

  // ГОЛОСОВОЕ сообщение — отдельный компактный вид: волна (если сервер дал),
  // кнопка play/pause и время. Имени файла и скорости здесь НЕТ (это запись,
  // а не музыкальный трек); расшифровка (footer) — остаётся.
  if (variant === 'voice') {
    const voiceBorder = mine ? 'border-primary/30 bg-primary/5' : 'border-border bg-muted/40';
    return (
      <div className={`w-full rounded-xl border px-2 py-1.5 ${voiceBorder}`}>
        {peaks.length > 0 && (
          <Waveform peaks={peaks} progress={progress} onSeek={(r) => seek(r * Math.max(duration, 0))} />
        )}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => (url ? toggle() : undefined)}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground hover:opacity-90"
            title={playing ? 'Пауза' : 'Воспроизвести голосовое'}
          >
            {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          </button>
          <div className="min-w-0 flex-1">
            {peaks.length === 0 && (
              <input
                type="range"
                min={0}
                max={Math.max(duration, 0.1)}
                step={0.01}
                value={current}
                onChange={(e) => seek(Number(e.target.value))}
                className="h-1.5 w-full cursor-pointer accent-primary"
                aria-label="Позиция"
              />
            )}
            <div className="flex items-center justify-between text-[10px] text-muted-foreground">
              <span>{fmt(current)} / {fmt(duration)}</span>
              <span className="opacity-70">голосовое</span>
            </div>
          </div>
          {onDownload && (
            <button type="button" onClick={onDownload} className="shrink-0 rounded p-1 hover:bg-black/5" title="Скачать">
              <Download className="h-3 w-3" />
            </button>
          )}
        </div>
        {url && (
          <audio
            ref={mediaRef as React.RefObject<HTMLAudioElement>}
            src={url}
            preload="metadata"
            {...handlers}
          />
        )}
        <MediaPlayerApiContext.Provider value={{ seek, current, duration, playing }}>
          {footer}
        </MediaPlayerApiContext.Provider>
      </div>
    );
  }

  // Аудио — премиум-контролы + waveform (единый вид в чате и просмотре файлов)
  const border = mine ? 'border-primary/30 bg-primary/5' : 'border-border bg-muted/40';
  return (
    <div className={`w-full rounded-xl border px-2.5 py-2 ${border}`}>
      {peaks.length > 0 && (
        <Waveform peaks={peaks} progress={progress} onSeek={(r) => seek(r * Math.max(duration, 0))} />
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => (url ? toggle() : undefined)}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground hover:opacity-90"
          title={playing ? 'Пауза' : 'Воспроизвести'}
        >
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </button>
        <div className="min-w-0 flex-1">
          {peaks.length === 0 && (
            <input
              type="range"
              min={0}
              max={Math.max(duration, 0.1)}
              step={0.01}
              value={current}
              onChange={(e) => seek(Number(e.target.value))}
              className="h-1.5 w-full cursor-pointer accent-primary"
              aria-label="Позиция"
            />
          )}
          <div className="mt-0.5 flex items-center justify-between text-[10px] text-muted-foreground">
            <span>{fmt(current)} / {fmt(duration)}</span>
            {name && <span className="max-w-[45%] truncate opacity-70">{name}</span>}
          </div>
        </div>
        <button type="button" onClick={cycleRate} className="flex shrink-0 items-center gap-0.5 rounded px-1 py-0.5 text-[10px] hover:bg-black/5" title="Скорость (запоминается для всех записей)">
          <Gauge className="h-3.5 w-3.5" />
          {rate}×
        </button>
        {!compact && (
          <button
            type="button"
            onClick={() => {
              const m = mediaRef.current;
              const next = !muted;
              setMuted(next);
              if (m) m.muted = next;
            }}
            className="shrink-0 rounded p-1 hover:bg-black/5"
            title={muted ? 'Включить звук' : 'Выключить звук'}
          >
            {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
          </button>
        )}
        {onDownload && (
          <button type="button" onClick={onDownload} className="shrink-0 rounded p-1 hover:bg-black/5" title="Скачать">
            <Download className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {url && (
        <audio
          ref={mediaRef as React.RefObject<HTMLAudioElement>}
          src={url}
          preload="metadata"
          {...handlers}
        />
      )}
      <MediaPlayerApiContext.Provider value={{ seek, current, duration, playing }}>
        {footer}
      </MediaPlayerApiContext.Provider>
    </div>
  );
}
